/* eslint-disable no-extra-boolean-cast */

import { NamespaceProvider, NamespaceConsumer } from '@ux/namespace-component';
import { breakpoints } from '@ux/component';
import BrowserDeprecationBanner from '@ux/browser-deprecation-banner';
import UtilityBar from '@ux/utility-bar';
import ProductNav from '@ux/product-nav';
import SalesFooter from '@ux/sales-footer';
import AbstractHeader from '@ux/abstract-header';
import ResellerSalesFooter from '@ux/reseller-sales-footer';
import { events } from '@ux/header-util';
import React from 'react';
import PropTypes from 'prop-types';

/**
 * Return a namespace if the environment variable is set, 'polyfilled' by WebPack.
 *
 * @returns {Object} Configured namespace.
 * @api private
 */
function namespace() {
  return {
    namespace: process.env.NAMESPACE // eslint-disable-line no-process-env
  };
}

/**
 * Render and expose the Header as a functional Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Namespaced Header.
 * @api public
 */
class Header extends AbstractHeader {
  constructor(props, ...args) {
    super(props, ...args);

    const { components = {}} = props;
    this.state = {
      ...this.state,
      productNav: true,
      unauthenticated: this.isUnauthenticated(components.utilityBar),
      backLink: props.backLink || null
    };

    this.setProductNav = this.setProductNav.bind(this);
    this.getCustomerState = this.getCustomerState.bind(this);
    this.updateBackLink = this.updateBackLink.bind(this);
  }

  /**
   * function to pass to utilityBar to allow it to control ProductNav from an
   * adjacent component
   *
   * @param {Boolean} open openstate of ProductNav
   * @public
   */
  setProductNav(open) {
    const { setProductNav, sticky } = this.props;
    if (sticky) {
      this.setState({ productNav: open });
      if (setProductNav) setProductNav(open);
    }
  }

  /**
   * Helper to update back link.
   *
   * @param {Object} data Updated back link object.
   * @param {Function} done Continuation to call when complete.
   * @api public
   */
  updateBackLink(data, done = () => {}) {
    this.setState({
      backLink: data
    }, done);
  }

  render() {
    return (
      <NamespaceProvider { ...namespace() } >
        <HeaderComponent
          { ...this.props } { ...this.state }
          setProductNav={ this.setProductNav }
          getCustomerState={ this.getCustomerState }
          customer={ this.customer } />
      </NamespaceProvider>
    );
  }
}

Header.propTypes = {
  navigation: PropTypes.array.isRequired,
  currentPage: PropTypes.string,
  preset: PropTypes.string,
  specialNav: PropTypes.array,
  supportContacts: PropTypes.object,
  showProductNav: PropTypes.bool,
  traffic: PropTypes.string,
  setProductNav: PropTypes.func,
  sticky: PropTypes.bool,
  market: PropTypes.string.isRequired,
  privateLabelId: PropTypes.number.isRequired
};

Header.defaultProps = {
  showProductNav: true
};

class HeaderComponent extends NamespaceConsumer {
  constructor(props, ...args) {
    super(props, ...args);

    /**
     * By default the sticky is closed and the productNav is visible. When the
     * sticky header is created on scroll, we don't render the productNav so it
     * can be open/closed by the menu button in the sticky utilityBar
     */
    this.state = {
      almostSticky: false,
      sticky: false,
      stickyHeight: 0
    };

    this.onScroll = this.onScroll.bind(this);
    this.resize = this.resize.bind(this);
    this.setTooSmall = this.setTooSmall.bind(this);
    this.setStickyHeight = this.setStickyHeight.bind(this);
    this.getCartProps = this.getCartProps.bind(this);
  }

  /**
   * Designated height for sticky header initiation. Defined as a setter so
   * that future updates can just change it one place
   */
  get stickyTriggerHeight() {
    return 320;
  }


  /**
  * Height at which the scroll gets past the navigation before sticky header gets initiated
  */
  get almostStickyheight() {
    return 120;
  }

  /**
   * scroll handler to properly trigger sticky state
   */
  onScroll() {
    if (!this.state.tooSmall) {
      const newAlmostSticky = window.scrollY > this.almostStickyheight;
      if (newAlmostSticky !== this.state.almostSticky) {
        this.setState({ almostSticky: newAlmostSticky });
      }

      const newSticky = window.scrollY > this.stickyTriggerHeight;
      if ((newSticky !== this.state.sticky) && !!this.props.sticky) {
        this.setState({ sticky: newSticky });
        this.props.setProductNav(!newSticky);
      }
    }
  }

  /**
   * The state is considered "too small" when it is below a desktop breakpoint.
   * Disabling the sticky functionality
   */
  setTooSmall() {
    this.setState({
      tooSmall: ['mobile', 'phablet', 'tablet'].indexOf(breakpoints.breakpoint) !== -1
    });
  }

  /**
   * monitor window to disable and reset sticky functionality
   * if they pass into small enough breakpoint
   */
  resize() {
    if (breakpoints.changed()) {
      this.setTooSmall();
      this.onScroll();
    }
  }

  /**
   * Update references tracking sticky height. These include:
   * - window.ux.eldorado.dockedHeaderHeight
   * - any listeners to the 'dockedHeaderHeightChanged' event
   * - this.state
   *
   * @param {Number} stickyHeight height of sticky header
   **/
  setStickyHeight(stickyHeight) {
    window.ux.eldorado.dockedHeaderHeight = stickyHeight;
    events.emit('dockedHeaderHeightChanged', stickyHeight);
    this.setState({ stickyHeight });
  }

  /**
   * Updates the stickyHeight if state.sticky is true. This
   * propagates the changes out to header consumers.
   */
  componentDidUpdate() {
    if (this.state.sticky) {
      const stickyHeight = this.headerElement.clientHeight;
      if (stickyHeight !== this.state.stickyHeight) {
        this.setStickyHeight(stickyHeight);
      }
    }
  }

  /*
   * Update the exposed `dockedHeaderHeight` if sticky nav is enabled.
   */
  componentWillUpdate(nextProps, nextState) {
    if (!this.props.sticky && (!nextProps || !nextProps.sticky) || !nextState) {
      return;
    }

    const { sticky } = this.state;
    const nextSticky = nextState.sticky;
    const body = document.body;

    // Do not attempt to make any changes when:
    // - sticky state has not changed.
    // - Not in a true DOM environment (i.e. Browser)
    // - Reference to wrapping <div> does not exist.
    if (sticky === nextSticky || !this.isBrowser || !this.headerElement) {
      return;
    }

    // If the body is becoming sticky `{ sticky: true }` then set top padding
    // on body so page doesn't jump when scrolling up or down.
    if (nextSticky && !sticky) {
      const previousPaddingTop = body.style.paddingTop;
      body.style.paddingTop = this.headerElement.clientHeight + 'px';
      this.setState({ previousPaddingTop });
      return;
    }

    // If the body is becoming "unsticky" `{ nextSticky: false }` then restore
    // the previous top padding on body if it exists.
    if (!nextSticky && sticky) {
      body.style.paddingTop = this.state.previousPaddingTop;
    }
  }

  /**
   * Attach event listener needed for sticky nav
   */
  componentDidMount() {
    if (this.props.sticky) {
      window.addEventListener('scroll', this.onScroll);
      window.addEventListener('resize', this.resize);
      this.setTooSmall();
      this.onScroll();
    }
  }

  /**
   * Detach event listener when not needed
   */
  componentWillUnmount() {
    if (this.props.sticky) {
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.resize);
    }
  }

  /**
   * Enable HelpSearchContactTray only for `en-US`, env DEV/TEST, loggedIn
   *
   * @returns {Boolean} isInCohort for DEV/TEST FOSSalesHeader
   * @param {Object} customerState object from our GUI call for the customer.
   **/
  shouldEnableHelpSearchTray(customerState) {
    const { preset, market } = this.props;
    const isFosSalesHeader = !preset;
    const isInCohort = customerState.loggedIn && market === 'en-US';
    return isFosSalesHeader && isInCohort;
  }

  getCartProps(customerState) {
    const { props } = this;
    const items = customerState.items;
    let prefix = 'uxp.hyd.sales_header.';
    let cartEid = 'cart_empty.link.click';
    if (props.seechange) {
      prefix = 'uxp.hyd.sales_header_seechange.';
    }
    if (items > 0) {
      cartEid = 'cart_full.link.click';
    }
    cartEid = prefix + cartEid;

    return {
      items,
      market: 'en-US',
      messages: props.messages,
      checkout: props.urls.checkout.href,
      eid: cartEid,
      className: this.namespace('cart-link'),
      showEmpty: true,
      textOnly: props.seechange && !items && !customerState.loggedIn
    };
  }

  render() {
    const { props, state } = this;
    const sticky = { ...state, productNav: this.props.productNav };

    const components = props.components;
    const ubComponents = components && components.utilityBar;

    const classNames = this.namespace(this.classNames(
      state.sticky && 'sticky-header',
      state.almostSticky && 'almost-sticky'
    ));

    // create a variable here for showing the self-help contact-tray for env(NOT PROD), market `en-US` and loggedIn
    const customerState = this.props.getCustomerState();
    const enableHelpSearchContactTray = this.shouldEnableHelpSearchTray(customerState);

    return (
      // eslint-disable-next-line no-return-assign
      <div className={ classNames } ref={ r => this.headerElement = r }>
        <BrowserDeprecationBanner { ...props } />
        <UtilityBar
          { ...props }
          { ...props.utilityBar }
          { ...customerState }
          cart={ this.getCartProps(customerState) }
          setProductNav={ this.props.setProductNav }
          enableHelpSearchContactTray={ enableHelpSearchContactTray }
          fullHelpLinkId={ 'Sales:HelpSplitA' }
          components={ ubComponents } />
        { props.showProductNav && <ProductNav { ...props }
          sticky={ sticky }
          components={ components && components.productNav } /> }
      </div>
    );
  }
}

class Footer extends React.Component {
  constructor(props, ...args) {
    super(props, ...args);
  }

  componentDidMount() {
    events.emit('mount', 'footer', this);
    events.emit('mount:footer', this);
  }

  render() {
    const {
      isReseller,
      ...props
    } = this.props;

    const footer = isReseller
      ? <ResellerSalesFooter { ...props } />
      : <SalesFooter { ...props } />;

    return (
      <NamespaceProvider { ...namespace() } >
        { footer }
      </NamespaceProvider>
    );
  }
}

Footer.propTypes = {
  isReseller: PropTypes.bool
};

Footer.defaultProps = {
  isReseller: false
};

export {
  Header,
  Footer
};
